// src/components/AnonymizationProfilesPage/AnonymizationProfileCard.jsx
import React from 'react';
import {
    Paper,
    Box,
    Typography,
    IconButton,
    Stack,
    Chip,
    Tooltip,
    Avatar,
    Divider,
} from '@mui/material';
import { Edit, Delete, Security } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import {
    GDPR_GROUPS,
    NIS2_GROUPS,
    VYHRADENE_GROUPS,
} from './AnonymizationProfileFormDialog/detectorCatalog';

// key -> label mapy z katalógu (zhodné s formulárom profilu)
const buildLabelMap = (groups) => {
    const m = {};
    groups.forEach((g) => g.detectors.forEach((d) => { m[d.key] = d.label; }));
    return m;
};
const NIS2_LABELS = buildLabelMap(NIS2_GROUPS);
const VYH_LABELS = buildLabelMap(VYHRADENE_GROUPS);
const GDPR_DETECTORS = GDPR_GROUPS.flatMap((g) => g.detectors); // poradie zachované

const chipSx = {
    borderRadius: '6px',
    fontSize: '0.72rem',
    fontWeight: 600,
    height: 22,
};

// Riadok jednej rodiny: malý nadpis + zoznam chipov zakliknutých detektorov
const FamilyRow = ({ caption, color, labels }) => {
    if (!labels.length) return null;
    return (
        <Box>
            <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: `${color}.main`, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.5 }}
            >
                {caption} · {labels.length}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                {labels.map((lbl, i) => (
                    <Chip key={`${caption}-${i}`} label={lbl} size="small" variant="outlined" color={color} sx={chipSx} />
                ))}
            </Stack>
        </Box>
    );
};

const AnonymizationProfileCard = ({ profile, onEdit, onDelete }) => {
    const { t } = useTranslation();
    const { name, description, options = {} } = profile;

    // --- zakliknuté detektory podľa rodín ---
    const gdprLabels = GDPR_DETECTORS.filter((d) => options[d.key]).map((d) => d.label);
    const nis2Labels = (options.nis2_detectors || []).map((k) => NIS2_LABELS[k] || k);
    const vyhLabels = (options.vyhradene_detectors || []).map((k) => VYH_LABELS[k] || k);
    const totalDetectors = gdprLabels.length + nis2Labels.length + vyhLabels.length;

    // --- správanie / režim (vedľajšie chipy) ---
    const behaviorChips = [];
    const customWords = options.custom_words || [];
    if (customWords.length > 0) {
        behaviorChips.push(
            <Chip key="custom" size="small" variant="outlined" sx={chipSx}
                label={t('anonymizationProfiles.customWordsCount', '{{count}} vlastných slov/fráz', { count: customWords.length })} />
        );
    }
    if (options.save_to_db) {
        behaviorChips.push(<Chip key="savedb" size="small" variant="outlined" color="success" sx={chipSx} label={t('anonymizationProfiles.saveDbShort', 'Ukladať do DB')} />);
    }
    if (options.text_anonymization_mode) {
        behaviorChips.push(<Chip key="text_mode" size="small" variant="outlined" sx={chipSx}
            label={t('anonymizationProfiles.textMode', 'Text: {{mode}}', { mode: options.text_anonymization_mode })} />);
    }
    if (options.signature_anonymization_mode) {
        behaviorChips.push(<Chip key="sig_mode" size="small" variant="outlined" sx={chipSx}
            label={t('anonymizationProfiles.sigMode', 'Podpis: {{mode}}', { mode: options.signature_anonymization_mode })} />);
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    borderColor: 'primary.main',
                    transform: 'translateY(-4px)',
                },
            }}
        >
            {/* header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: 'action.selected', color: 'primary.main', flexShrink: 0, borderRadius: 2 }}>
                    <Security fontSize="small" />
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0, mt: 0.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                        {name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontWeight: 500 }}>
                        {totalDetectors > 0
                            ? t('anonymizationProfiles.detectorsCount', '{{count}} detektorov', { count: totalDetectors })
                            : t('anonymizationProfiles.rulesCountEmpty', 'Žiadne vybrané detektory')}
                    </Typography>
                    {description && (
                        <Typography variant="body2"
                            sx={{ mt: 1, color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.5 }}>
                            {description}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title={t('anonymizationProfiles.editTooltip', 'Upraviť profil')}>
                        <IconButton size="small" color="primary" onClick={() => onEdit(profile)}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('anonymizationProfiles.deleteTooltip', 'Odstrániť profil')}>
                        <IconButton size="small" color="error" onClick={() => onDelete(profile)}>
                            <Delete fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* zoznam zakliknutých detektorov podľa rodín */}
            <Box sx={{ mt: 'auto' }}>
                {totalDetectors === 0 && behaviorChips.length === 0 ? (
                    <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                        {t('anonymizationProfiles.noOptionsSummary', 'Bez vybraných detektorov (použijú sa predvolené hodnoty).')}
                    </Typography>
                ) : (
                    <Stack spacing={1.25}>
                        <FamilyRow caption="GDPR" color="primary" labels={gdprLabels} />
                        <FamilyRow caption="NIS2" color="warning" labels={nis2Labels} />
                        <FamilyRow caption="VYHRADENÉ" color="error" labels={vyhLabels} />
                        {behaviorChips.length > 0 && (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                {behaviorChips}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>
        </Paper>
    );
};

export default AnonymizationProfileCard;
